<template>
  <div class="app-layout">
    <AppHeader />
    
    <main class="main-content">
      <slot />
    </main>
    
    <!-- Quick Actions Sidebar -->
    <aside class="quick-actions" :class="{ 'sidebar-open': isSidebarOpen }">
      <!-- Mobile Close Button -->
      <button 
        v-if="isMobile" 
        @click="closeSidebar" 
        class="close-sidebar-btn"
        aria-label="Close sidebar"
      >
        <Icon name="mdi:close" />
      </button>
      
      <div class="quick-actions-content">
        <h3>Quick Actions</h3>
        <div class="action-buttons">
          <NuxtLink to="/standings" class="action-btn" @click="closeSidebar">
            <Icon name="mdi:chart-line" />
            <span>View Standings</span>
          </NuxtLink>
          <NuxtLink to="/contact" class="action-btn" @click="closeSidebar">
            <Icon name="mdi:account-plus" />
            <span>Join League</span>
          </NuxtLink>
          <a href="mailto:theplayersleaguedfw@gmail.com" class="action-btn" @click="closeSidebar">
            <Icon name="mdi:email" />
            <span>Contact Us</span>
          </a>
        </div>
        
        <div class="league-info-card">
          <h4>Current Season</h4>
          <div class="info-item">
            <Icon name="mdi:calendar" />
            <span>2025 Season Active</span>
          </div>
          <div class="info-item">
            <Icon name="mdi:map-marker" />
            <span>2 Locations</span>
          </div>
          <div class="info-item">
            <Icon name="mdi:trophy" />
            <span>Bad Boys Format</span>
          </div>
        </div>

        <div class="legend-card">
          <h4>Stats Legend</h4>
          <div class="legend-item">
            <span class="legend-abbr">WIN PCT</span>
            <span class="legend-desc">Win percentage</span>
          </div>
          <div class="legend-item">
            <span class="legend-abbr">PTS F/A</span>
            <span class="legend-desc">Points For/Against</span>
          </div>
          <div class="legend-item">
            <span class="legend-abbr">GMS Won</span>
            <span class="legend-desc">Games won</span>
          </div>
          <div class="legend-item">
            <span class="legend-abbr">WKs</span>
            <span class="legend-desc">Weeks played</span>
          </div>
          <div class="legend-item">
            <span class="legend-abbr">BRs</span>
            <span class="legend-desc">Break and runs</span>
          </div>
          <div class="legend-item">
            <span class="legend-abbr">WZs</span>
            <span class="legend-desc">Win zips (opponent has 0 points)</span>
          </div>
          <div class="legend-item">
            <span class="legend-abbr">TRs</span>
            <span class="legend-desc">Table runs</span>
          </div>
          <div class="legend-item">
            <span class="legend-abbr">WBs</span>
            <span class="legend-desc">Win on the break (8/9 on break- when applicable)</span>
          </div>
          <div class="legend-item">
            <span class="legend-abbr">CWs</span>
            <span class="legend-desc">Consecutive Wins (games... running total from week to week)</span>
          </div>
          <div class="legend-item">
            <span class="legend-abbr">PMs</span>
            <span class="legend-desc">Perfect matches (no losses for the night)</span>
          </div>
        </div>
      </div>
    </aside>
    
    <!-- Mobile Sidebar Toggle Button -->
    <button 
      v-if="isMobile" 
      @click="toggleSidebar" 
      class="sidebar-toggle-btn"
      aria-label="Toggle quick actions sidebar"
    >
      <Icon name="mdi:menu" />
      <span class="toggle-label">Info</span>
    </button>
    
    <!-- Mobile Sidebar Overlay -->
    <div 
      v-if="isMobile && isSidebarOpen" 
      @click="closeSidebar" 
      class="sidebar-overlay"
    ></div>
    
    <AppFooter />
    
    <!-- Back to Top Button for Mobile -->
    <BackToTop />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';

const isMobile = ref(false);
const isSidebarOpen = ref(false);

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 1024;
  // Close sidebar if window is resized to desktop
  if (!isMobile.value) {
    isSidebarOpen.value = false;
  }
};

const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value;
  // Prevent body scroll when sidebar is open
  if (isSidebarOpen.value) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
};

const closeSidebar = () => {
  isSidebarOpen.value = false;
  document.body.style.overflow = '';
};

onMounted(() => {
  checkMobile();
  window.addEventListener('resize', checkMobile);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile);
  document.body.style.overflow = '';
});
</script>

<style scoped>
.app-layout {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1fr 280px;
  grid-template-rows: auto 1fr auto;
  grid-template-areas: 
    "header header"
    "main sidebar"
    "footer footer";
  gap: 0;
  overflow-x: hidden;
  width: 100%;
  max-width: 100vw;
}

.app-header {
  grid-area: header;
}

.main-content {
  grid-area: main;
  padding: 0;
  position: relative;
  overflow-x: hidden;
  width: 100%;
  max-width: 100%;
}

.quick-actions {
  grid-area: sidebar;
  background: linear-gradient(180deg, var(--navy-blue) 0%, var(--dark-blue) 100%);
  border-left: 2px solid rgba(255, 184, 28, 0.2);
  padding: 24px;
  position: fixed;
  top: 0;
  right: 0;
  width: 280px;
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
}

.quick-actions-content h3 {
  color: var(--gold);
  font-size: 1.2rem;
  margin-bottom: 20px;
  font-family: var(--font-heading);
  text-align: center;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 32px;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 184, 28, 0.2);
  border-radius: 8px;
  color: var(--cream);
  text-decoration: none;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(5px);
}

.action-btn:hover {
  background: rgba(255, 184, 28, 0.1);
  border-color: var(--gold);
  color: var(--gold);
  transform: translateX(4px);
}

.league-info-card {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid rgba(255, 184, 28, 0.2);
}

.league-info-card h4 {
  color: var(--gold);
  font-size: 1rem;
  margin-bottom: 16px;
  text-align: center;
  font-family: var(--font-heading);
}

.info-item {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--cream);
  font-size: 0.85rem;
  margin-bottom: 12px;
  opacity: 0.9;
}

.info-item:last-child {
  margin-bottom: 0;
}

.legend-card {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid rgba(255, 184, 28, 0.2);
  margin-top: 24px;
}

.legend-card h4 {
  color: var(--gold);
  font-size: 1rem;
  margin-bottom: 16px;
  text-align: center;
  font-family: var(--font-heading);
}

.legend-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 184, 28, 0.1);
}

.legend-item:last-child {
  margin-bottom: 0;
  border-bottom: none;
}

.legend-abbr {
  color: var(--gold);
  font-weight: 600;
  font-size: 0.9rem;
  font-family: var(--font-heading);
}

.legend-desc {
  color: var(--cream);
  font-size: 0.8rem;
  opacity: 0.9;
  line-height: 1.3;
}

.app-footer {
  grid-area: footer;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .quick-actions {
    padding: 20px;
  }
}

@media (max-width: 1024px) {
  .app-layout {
    grid-template-columns: 1fr;
    grid-template-areas: 
      "header"
      "main"
      "footer";
  }
  
  .quick-actions {
    position: fixed;
    top: 0;
    right: -100%;
    height: 100vh;
    width: 320px;
    max-width: 85vw;
    z-index: 1000;
    border-left: 2px solid var(--gold);
    box-shadow: -4px 0 20px rgba(0, 0, 0, 0.5);
    transition: right 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    overflow-y: auto;
    padding: 60px 20px 20px;
  }
  
  .quick-actions.sidebar-open {
    right: 0;
  }
  
  .close-sidebar-btn {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 40px;
    height: 40px;
    background: rgba(255, 184, 28, 0.15);
    border: 1px solid var(--gold);
    border-radius: 50%;
    color: var(--gold);
    font-size: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    z-index: 10;
  }
  
  .close-sidebar-btn:hover {
    background: rgba(255, 184, 28, 0.25);
    transform: rotate(90deg);
  }
  
  .sidebar-toggle-btn {
    position: fixed;
    bottom: 90px;
    right: 20px;
    width: 56px;
    height: 56px;
    background: linear-gradient(135deg, var(--gold), #d4a027);
    border: 2px solid rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    color: var(--navy-blue);
    font-size: 1.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
    transition: all 0.3s ease;
    z-index: 999;
  }
  
  .sidebar-toggle-btn:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(255, 184, 28, 0.5);
  }
  
  .sidebar-toggle-btn:active {
    transform: scale(0.95);
  }
  
  .toggle-label {
    font-size: 0.7rem; /* Increased from 0.6rem to meet 12px minimum */
    font-weight: 700;
    margin-top: 2px;
    text-transform: uppercase;
  }
  
  .sidebar-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    z-index: 999;
    animation: fadeIn 0.3s ease;
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  
  .quick-actions-content {
    max-width: 100%;
  }
  
  .action-buttons {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
}

@media (max-width: 768px) {
  .quick-actions {
    padding: 60px 16px 16px;
    width: 300px;
  }
  
  .sidebar-toggle-btn {
    width: 50px;
    height: 50px;
    bottom: 80px;
    right: 16px;
  }
  
  .league-info-card {
    padding: 16px;
  }
}

@media (max-width: 480px) {
  .quick-actions {
    width: 280px;
  }
  
  .sidebar-toggle-btn {
    width: 48px;
    height: 48px;
    bottom: 75px;
    right: 14px;
  }
  
  .toggle-label {
    font-size: 0.55rem;
  }
}
</style>
